import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import guestApi from '../utils/guestApi';
import styles from './GuestMergeNoticeBanner.module.css';

const NOTICE_KEY = 'velox_guest_merge_notice';
const DISMISS_KEY = 'velox_guest_merge_banner_dismissed';

function readNotice() {
  try {
    const raw = sessionStorage.getItem(NOTICE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

/**
 * Subtle recovery prompt when guest vault merge was partial or cookie still has pending saves.
 */
export default function GuestMergeNoticeBanner() {
  const [visible, setVisible] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      setVisible(false);
      return undefined;
    }

    let cancelled = false;

    (async () => {
      const notice = readNotice();

      try {
        const { data } = await guestApi.get('/api/guest/status');
        if (cancelled) return;

        const pending =
          data?.state === 'pending_merge' && typeof data.pendingMergeCount === 'number'
            ? data.pendingMergeCount
            : 0;

        if (data?.state !== 'pending_merge' || pending === 0) {
          try {
            sessionStorage.removeItem(DISMISS_KEY);
          } catch {
            /* ignore */
          }
        }

        const dismissed = sessionStorage.getItem(DISMISS_KEY) === '1';
        const hardNotice = notice && (notice.failed > 0 || notice.pendingRetry);
        const needsAttention = hardNotice || pending > 0;

        if (!needsAttention) {
          setVisible(false);
          return;
        }

        if (dismissed && !hardNotice) {
          setVisible(false);
          return;
        }

        if (hardNotice) {
          setMessage(
            'Some contacts from Save to Velox did not finish syncing. Nothing was removed — sign out and sign back in with the same email, or try again in a little while.'
          );
        } else if (pending > 0) {
          setMessage(
            `You have ${pending} saved contact${pending === 1 ? '' : 's'} waiting to move into your account. Sign out and sign back in with the same email you used for Save to Velox.`
          );
        }

        setVisible(true);
      } catch {
        if (cancelled) return;
        const dismissed = sessionStorage.getItem(DISMISS_KEY) === '1';
        if (notice && (notice.failed > 0 || notice.pendingRetry) && !dismissed) {
          setMessage(
            'Some contacts from Save to Velox may still be syncing. Sign in again with the same email if anything looks missing.'
          );
          setVisible(true);
        } else {
          setVisible(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const dismiss = () => {
    try {
      sessionStorage.setItem(DISMISS_KEY, '1');
      sessionStorage.removeItem(NOTICE_KEY);
    } catch {
      /* ignore */
    }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div className={styles.wrap} role="status">
      <p className={styles.text}>{message}</p>
      <div className={styles.actions}>
        <Link to="/contacts" className={styles.link}>
          View contacts
        </Link>
        <button type="button" className={styles.dismiss} onClick={dismiss}>
          Dismiss
        </button>
      </div>
    </div>
  );
}
